import { endsWith, includes } from 'ramda';
import { z } from 'zod';

import { commonErrors } from '@utils/error';
import { doesNotExist } from '@utils/general';
import { isSelected, optionalString, superRefineHelper } from '@utils/validation';
import { IMedicationSelectionStepPageContent } from '@ui/routes/Estrogen/MedicationSelectionStep/MedicationSelectionStep.component';

export const dataConstants = {
	ROUTE: 'estrogenRouteType',
	MEDICATION: 'estrogenMedicationType',
	TYPE: 'estrogenInjectableType',
};

export const schema = z
	.object({
		[dataConstants.ROUTE]: optionalString,
		[dataConstants.MEDICATION]: optionalString,
		[dataConstants.TYPE]: optionalString,
	})
	.superRefine((data, ctx) => {
		const commonParams = { data: data, context: ctx };
		const routeSelected = isSelected(data?.[dataConstants.ROUTE] ?? '');
		const medicationSelected = isSelected(data?.[dataConstants.MEDICATION] ?? '');
		const isInjectable = endsWith('.injectable', data?.[dataConstants.ROUTE] ?? '');

		superRefineHelper({
			...commonParams,
			dataPath: dataConstants.ROUTE,
			negativeCondition: doesNotExist,
			message: commonErrors.selectionRequired,
		});
		if (routeSelected) {
			if (isInjectable) {
				superRefineHelper({
					...commonParams,
					dataPath: dataConstants.MEDICATION,
					negativeCondition: doesNotExist,
					message: commonErrors.selectionRequired,
				});
				if (medicationSelected) {
					superRefineHelper({
						...commonParams,
						dataPath: dataConstants.TYPE,
						negativeCondition: doesNotExist,
						message: commonErrors.selectionRequired,
					});
				}
			}
		}
	});

export const testMeds: string[] = [
	'hrt_e.oral.estradiol',
	'hrt_e.patch.estradiol',
	'hrt_e.injectable.valerate40',
	'hrt_e.injectable.estradiol_cypionate',
];

export const testContent: IMedicationSelectionStepPageContent = {
	pageHeader: "Let's select your Estrogen medication",
	pageInfo: '<p>You don&rsquo;t need to already know this information.</p>',
	routes: {
		routeHeader: 'How would you like to take estrogen?',
		routeTypes: [
			{
				label: 'Sublingual',
				description:
					'<p>Estrogen in pill form, taken once or multiple times a day, dissolved under the tongue.</p>',
				key: 'hrt_e.oral.estradiol',
			},
			{
				label: 'Injectable',
				description: '<p>Weekly or every-other week, self-administered hormone injection.</p>',
				key: 'hrt_e.injectable',
			},
			{
				label: 'Transdermal patch',
				description:
					'<p>One or multiple patches applied every 3-4 days allowing estrogen to be absorbed through the skin and into the bloodstream.</p>',
				key: 'hrt_e.patch.estradiol',
			},
			{
				label: "I don't know",
				description:
					'<p>That&rsquo;s okay! You&rsquo;ll be able to meet with a clinician to discuss your available options</p>',
				key: 'hrt_e.dont_know',
			},
		],
	},
	sublingual: {
		frequencyHeader: '',
		doseHeader: '',
		sublingualFrequencies: [
			{
				label: 'Once a day',
				key: 'hrt_e.oral.estradiol.oad',
			},
			{
				label: 'Multiple times a day',
				key: 'hrt_e.oral.estradiol.multiple',
			},
			{
				label: "I don't know",
				key: 'hrt_e.oral.estradiol.dont_know',
			},
		],
	},
	injectables: {
		typeHeader: '',
		medHeader: '',
		frequencyHeader: '',
		doseHeader: '',
		injectableMeds: [
			{
				label: 'Estradiol Valerate 40mg/mL',
				description: '<p>Most common for high doses</p>',
				key: 'hrt_e.injectable.valerate40',
			},
			{
				label: 'Estradiol Cypionate',
				description: '<p>PLEASE REPLACE THIS WITH <strong>GOOD CONTENT</strong>&trade;</p>',
				key: 'hrt_e.injectable.estradiol_cypionate',
			},
			{
				label: "I don't know",
				description: '<p>No text</p>',
				key: 'hrt_e.injectable.dont_know',
			},
		],
		injectableTypes: [
			{
				label: 'Subcutaneous',
				description: '<p>Most common. Inject into fatty tissue under the skin.</p>',
				key: 'hrt_e.injectable_type.subq',
			},
			{
				label: 'Intramuscular',
				description: '<p>Inject into muscle.</p>',
				key: 'hrt_e.injectable_type.intramusc',
			},
			{
				label: "I don't know",
				description: '',
				key: 'hrt_e.injectable_type.dont_know',
			},
		],
		injectableFrequencies: [
			{
				label: 'Weekly - Most common',
				key: 'hrt_e.injectable_frequency.weekly',
			},
			{
				label: 'Every other week',
				key: 'hrt_e.injectable_frequency.bimonthly',
			},
			{
				label: "I don't know",
				key: 'hrt_e.injectable_frequency.dont_know',
			},
		],
	},
};
